import React, { useState } from 'react';
import { CheckCircle2, Circle, Wifi, Globe, Mail, DollarSign, FileText, MessageSquare, Loader2, AlertCircle, ChevronRight, Building2, Router, Signal } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface Task {
  id: string;
  title: string;
  description: string;
  completed: boolean;
}

function PousadaInternetPM() {
  const [researchTasks, setResearchTasks] = useState<Task[]>([
    { id: 'current-setup', title: 'Document Current Setup', description: 'ISP, router model, speed plan, coverage areas', completed: false },
    { id: 'identify-issues', title: 'Identify Main Issues', description: 'Speed problems, dead zones, capacity limits', completed: false },
    { id: 'budget-range', title: 'Define Budget Range', description: 'Get budget approval from management', completed: false },
    { id: 'coverage-map', title: 'Map Coverage Requirements', description: 'Number of rooms, common areas, outdoor spaces', completed: false },
    { id: 'user-analysis', title: 'Analyze User Load', description: 'Peak users, typical usage patterns', completed: false }
  ]);

  const [vendorTasks, setVendorTasks] = useState<Task[]>([
    { id: 'vivo-quote', title: 'Vivo Fibra', description: 'Request business plan quote', completed: false },
    { id: 'claro-quote', title: 'Claro NET', description: 'Compare fiber options', completed: false },
    { id: 'local-isps', title: 'Local ISPs', description: 'Research regional providers', completed: false },
    { id: 'equipment-quotes', title: 'Equipment Quotes', description: 'Routers, mesh systems, extenders', completed: false },
    { id: 'installation', title: 'Installation Services', description: 'Professional network setup quotes', completed: false }
  ]);

  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // AI is always available via API endpoint

  const portugueseTerms = [
    { pt: 'Internet fibra óptica', en: 'Fiber optic internet' },
    { pt: 'Roteador', en: 'Router' },
    { pt: 'Rede mesh', en: 'Mesh network' },
    { pt: 'Velocidade', en: 'Speed' },
    { pt: 'Plano empresarial', en: 'Business plan' },
    { pt: 'Orçamento', en: 'Quote' },
    { pt: 'Instalação', en: 'Installation' },
    { pt: 'Suporte técnico', en: 'Technical support' }
  ];

  const toggleTask = (taskList: Task[], setTaskList: React.Dispatch<React.SetStateAction<Task[]>>, taskId: string) => {
    setTaskList(taskList.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ));
  };

  const calculateProgress = () => {
    const allTasks = [...researchTasks, ...vendorTasks];
    const completedTasks = allTasks.filter(task => task.completed).length;
    return Math.round((completedTasks / allTasks.length) * 100);
  };

  const getDemoResponse = (query: string) => {
    const lowerQuery = query.toLowerCase();
    
    if (lowerQuery.includes('email') || lowerQuery.includes('portuguese')) {
      return `Here's a professional email template in Portuguese:

Assunto: Solicitação de Orçamento - Internet Fibra Óptica para Pousada

Prezados,

Somos uma pousada localizada em [CIDADE] e estamos interessados em contratar um plano de internet fibra óptica empresarial.

Nossas necessidades:
- Velocidade mínima: [X] Mbps
- Número de quartos: [X]
- Áreas comuns: [LISTAR]
- Usuários simultâneos: [X]

Gostaríamos de receber:
1. Opções de planos disponíveis
2. Valores mensais
3. Taxa de instalação
4. Prazo para instalação
5. SLA e suporte técnico

Atenciosamente,
[SEU NOME]`;
    } else if (lowerQuery.includes('mesh')) {
      return `Mesh Network Benefits for Pousadas:

1. **Seamless Coverage**: Eliminates dead zones across your property
2. **Easy Expansion**: Add nodes as needed
3. **Self-Healing**: Automatic rerouting if a node fails
4. **Guest Experience**: No connection drops while moving

Recommended Systems:
- TP-Link Deco (good value)
- Ubiquiti UniFi (professional)
- ASUS AiMesh (flexible)`;
    } else {
      return `I'll help with your pousada internet upgrade project.

For "${query}", consider:
1. Document current setup thoroughly
2. Fiber optic (fibra óptica) for reliability
3. Business plans include better SLA
4. Plan for future growth

Use "Gostaria de solicitar um orçamento" when requesting quotes.`;
    }
  };

  const handleAiRequest = async () => {
    if (!aiQuery.trim()) return;

    setIsLoading(true);
    setError('');
    setAiResponse('');

    try {
      // Use relative URL - works for both dev (with proxy) and production (with custom domain)
      const apiUrl = '/api/ai/pousada-assistant';
        
      console.log('🌍 API URL:', apiUrl);
        
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: aiQuery }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setAiResponse(data.response);
      } else {
        throw new Error(data.error || 'Failed to get AI response');
      }
    } catch (err) {
      console.error('AI request error:', err);
      setError('Failed to connect to AI. Using demo response.');
      setAiResponse(getDemoResponse(aiQuery));
    } finally {
      setIsLoading(false);
      setAiQuery('');
    }
  };

  const TaskList = ({ tasks, setTasks }: { tasks: Task[]; setTasks: React.Dispatch<React.SetStateAction<Task[]>> }) => (
    <div className="space-y-3">
      {tasks.map((task: Task) => (
        <div
          key={task.id}
          onClick={() => toggleTask(tasks, setTasks, task.id)}
          className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
            task.completed 
              ? 'bg-green-50 border-green-200 hover:border-green-300' 
              : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
          }`}
        >
          <div className="flex items-start gap-3">
            {task.completed ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <Circle className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1">
              <h4 className={`font-medium ${task.completed ? 'text-green-800' : 'text-gray-900'}`}>
                {task.title}
              </h4>
              <p className={`text-sm mt-1 ${task.completed ? 'text-green-600' : 'text-gray-600'}`}>
                {task.description}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Wifi className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Pousada Internet Upgrade</h1>
          </div>
          <p className="text-gray-600 mb-4">Project Management & AI Assistant</p>
          
          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Overall Progress</span>
              <span className="text-sm font-medium text-gray-900">{calculateProgress()}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${calculateProgress()}%` }}
              />
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Research Checklist */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-6">
              <FileText className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Research Checklist</h2>
            </div>
            <TaskList tasks={researchTasks} setTasks={setResearchTasks} />
          </div>

          {/* Vendor Outreach */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-6">
              <DollarSign className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Vendor Outreach</h2>
            </div>
            <TaskList tasks={vendorTasks} setTasks={setVendorTasks} />
          </div>
        </div>

        {/* Portuguese Terms */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center gap-2 mb-6">
            <Globe className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Key Portuguese Terms</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {portugueseTerms.map((term, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-gray-900">{term.pt}</p>
                <p className="text-sm text-gray-600 mt-1">{term.en}</p>
              </div>
            ))}
          </div>
        </div>

        {/* AI Assistant */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">AI Assistant</h2>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
              AI Connected via API
            </span>
          </div>
          
          <p className="text-gray-600 mb-6">
            Ask questions about the project, get Portuguese translations, or request help with vendor communications.
          </p>

          <div className="flex gap-3 mb-6">
            <input
              type="text"
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAiRequest()}
              placeholder="E.g., 'Write a Portuguese email requesting fiber optic quotes'"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleAiRequest}
              disabled={isLoading || !aiQuery.trim()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing
                </>
              ) : (
                <>
                  Ask AI
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {aiResponse && (
            <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
              <div className="prose prose-sm max-w-none text-gray-700">
                <ReactMarkdown>{aiResponse}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PousadaInternetPM;